/**
 * Retrait du jeton et du marqueur du handoff de l'adresse (docs/ux-v2/HANDOFF.md § 3.4), AVANT l'init de Sentry.
 *
 * Module à part, minuscule, parce que main.tsx l'importe : il est donc dans le paquet principal même v2 éteinte (où il
 * n'est pas appelé). Le reste du handoff (./handoff.ts) ne vit qu'avec la v2.
 */

/** Marqueur posé par S sur les liens vers D : site de S (aucun secret). */
export const SSO_PARAM = 'mm_sso';

/** Jeton signé posé par S (handoff/authorize) sur la redirection vers D. */
export const TOKEN_PARAM = 'mm_handoff';

/** = UxV2Handoff::TOKEN_PATTERN. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,2000}\.[A-Za-z0-9_-]{43}$/;

const SITE_PATTERN = /^[1-9]\d{0,9}$/;

export interface CapturedHandoff {
  src: string | null;
  token: string | null;
}

const NONE: CapturedHandoff = { src: null, token: null };

let captured: CapturedHandoff = NONE;

/**
 * Retire `mm_sso` et `mm_handoff` de la partie « requête » d'une route du HashRouter (`#/class/1?mm_sso=…`).
 * Fonction pure ; `hash` inchangé si aucun des deux n'y est.
 */
export function stripHandoffParams(hash: string): { hash: string; src: string | null; token: string | null } {
  const at = hash.indexOf('?');
  if (at < 0) return { hash, src: null, token: null };
  const params = new URLSearchParams(hash.slice(at + 1));
  const src = params.get(SSO_PARAM);
  const token = params.get(TOKEN_PARAM);
  if (src === null && token === null) return { hash, src: null, token: null };
  params.delete(SSO_PARAM);
  params.delete(TOKEN_PARAM);
  const rest = params.toString();
  return { hash: hash.slice(0, at) + (rest ? `?${rest}` : ''), src, token };
}

/**
 * main.tsx, v2 active, AVANT l'init de Sentry : marqueur et jeton quittent l'adresse (history.replaceState) et ne
 * restent qu'en mémoire, jusqu'à takeCapturedHandoff. Même principe que le `paymentAccessToken` des portefeuilles.
 */
export function captureHandoffFromUrl(): void {
  if (typeof window === 'undefined') return;
  const { hash, src, token } = stripHandoffParams(window.location.hash);
  if (src === null && token === null) return;
  captured = {
    src: src !== null && SITE_PATTERN.test(src) ? src : null,
    token: token !== null && TOKEN_PATTERN.test(token) ? token : null,
  };
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${hash}`);
  } catch {
    // Adresse non réécrite : le jeton reste lié à la session de ce navigateur sur ce site (HANDOFF.md § 2).
  }
}

/** Ce qui a été capturé (une seule fois : la capture est vidée). */
export function takeCapturedHandoff(): CapturedHandoff {
  const value = captured;
  captured = NONE;
  return value;
}
